import {
    Children,
    cloneElement,
    createContext,
    isValidElement,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import { atomFamily, useRecoilState, useSetRecoilState } from 'recoil';

import {
    Actions,
    Card,
    CardBody,
    OverlayCloseButton,
    OverlayTriggerContext,
    Popover,
    PopoverArrow,
    PopoverRoot,
} from '@gitbook/spine-react';
import { useMergedRefs } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import { persistRecoilEffect } from '../../utils';

interface GuidedTour {
    step: string | null;
}

const guidedTourAtom = atomFamily<GuidedTour, string>({
    key: 'guidedTours',
    default: () => ({ step: null }),
    effects: [persistRecoilEffect({ initAsync: false })],
});

const START_STEP = 'start' as const;

const GuidedTourContext = createContext<{
    hasNextStep: boolean;
} | null>(null);

/**
 * Hook to interact with a guided tour.
 */
export function useGuidedTour(name: string) {
    const setTour = useSetRecoilState(guidedTourAtom(name));
    return useMemo(
        () => ({
            markAsDone() {
                setTour({ step: 'done' });
            },
        }),
        [setTour]
    );
}

interface GuidedTourStepProps {
    /**
     * Name of the tour, used for persistence.
     */
    tour: string;

    /**
     * Step of the tour.
     */
    step: 'start' | string;

    /**
     * Next step of the tour.
     */
    nextStep?: string;

    /**
     * Whether to skip this step (for example if the feature is not available).
     * We don't support yet skipping just one step and going to the next one.
     */
    skip?: boolean;

    /**
     * Props to pass to the underlying PopoverRoot.
     */
    children: React.ReactNode;
}

/**
 * Component to define a step within a guided tour.
 * @example
 * <GuidedTourStep tour="my-tour" step="start">
 *   <GuidedTourReference>
 *     <Button>Click me</Button>
 *   </GuidedTourReference>
 *   <GuidedTourPopover>
 *     This is a popover for the guided tour step.
 *   </GuidedTourPopover>
 * </GuidedTourStep>
 */
export function GuidedTourStep(props: GuidedTourStepProps) {
    const { tour: tourName = 'default', step, nextStep, skip = false, ...rest } = props;
    const [tour, setTour] = useRecoilState(guidedTourAtom(tourName));
    const isOpen = !skip && ((tour.step === null && step === START_STEP) || tour.step === step);

    useEffect(() => {
        if (isOpen && step !== START_STEP) {
            setTour({ step });
        }
    }, [isOpen, setTour, step]);

    const hasNextStep = Boolean(nextStep);
    const ctxValue = useMemo(() => ({ hasNextStep }), [hasNextStep]);

    return (
        <PopoverRoot
            isOpen={isOpen}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    setTour({ step: nextStep ?? 'done' });
                }
            }}
            {...rest}
        >
            <GuidedTourContext.Provider value={ctxValue}>
                {rest.children}
            </GuidedTourContext.Provider>
        </PopoverRoot>
    );
}

/**
 * Reference element to anchor the guided tour popover.
 * Must be used within a GuidedTourStep.
 */
export function GuidedTourReference(props: { children: React.ReactElement }) {
    const context = useContext(OverlayTriggerContext);
    assert(context !== null, 'GuidedTourReference must be used within a GuidedTourStep');
    const child = Children.only(props.children);
    assert(
        isValidElement<{ ref: React.RefCallback<HTMLElement | null>; style: React.CSSProperties }>(
            child
        ),
        'GuidedTourReference must have a single React element as its child.'
    );
    const mergeRef = useMergedRefs<HTMLElement>(context.ref, (child as any).ref);
    return cloneElement(child, { ref: mergeRef, style: { zIndex: 100 } });
}

function Spotlight(props: { targetRef: React.RefObject<HTMLElement | null> }) {
    const { targetRef } = props;
    const [borderRadius, setBorderRadius] = useState<string | undefined>(undefined);
    const [rect, setRect] = useState<DOMRect | null>(null);
    useLayoutEffect(() => {
        const target = targetRef.current;
        if (!target) {
            return;
        }
        const borderRadius = getComputedStyle(target).borderRadius;
        setBorderRadius(borderRadius);
        const update = () => {
            setRect(target.getBoundingClientRect());
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(target);
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            ro.disconnect();
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [targetRef]);

    if (!rect) {
        return null;
    }

    return (
        <div
            aria-hidden
            style={{
                position: 'fixed',
                left: rect.left,
                top: rect.top,
                width: rect.width,
                height: rect.height,
                borderRadius,
                // darken the rest of the screen
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
                outline: '2px solid white',
                pointerEvents: 'none',
            }}
        />
    );
}

/**
 * Popover content to display within a guided tour.
 * Must be used within a GuidedTourStep.
 */
export function GuidedTourPopover(props: { children: React.ReactNode; className?: string }) {
    const { children, className } = props;
    const tour = useContext(GuidedTourContext);
    const overlayTrigger = useContext(OverlayTriggerContext);
    assert(
        overlayTrigger !== null && tour !== null,
        'GuidedTourPopover must be used within a GuidedTourStep'
    );
    return (
        <Popover
            offset={12}
            className={className}
            backdrop={<Spotlight targetRef={overlayTrigger.ref} />}
        >
            <PopoverArrow />
            <Card>
                <CardBody>
                    <div>{children}</div>
                    <Actions className="self-stretch">
                        <OverlayCloseButton
                            slot="next"
                            kind="secondary"
                            label={tour.hasNextStep ? 'Next' : 'Close'}
                        />
                    </Actions>
                </CardBody>
            </Card>
        </Popover>
    );
}
